import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, MapPin, IndianRupee, Printer, Users } from 'lucide-react';
import { API, useAuth } from '../App';

const inr = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;

const monthLabel = (ym) => {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};

const PERIOD_LABELS = { today: 'Today', '7d': 'Last 7 days', month: 'This month', all: 'All time' };

export default function PartnerDashboard() {
  const { user, token, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ov, st] = await Promise.all([
        axios.get(`${API}/partner/overview`, { headers }),
        axios.get(`${API}/partner/statement`, { headers }),
      ]);
      setOverview(ov.data);
      setStatement(st.data);
    } catch (e) {
      // interceptor surfaces the toast
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="min-h-screen bg-cream-50" data-testid="partner-dashboard">
      {/* Header */}
      <header className="bg-charcoal-900 text-white px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/piczoo-logo.png" alt="Piczoo" className="h-6 sm:h-7 object-contain" />
          <span className="text-white/50 text-sm hidden sm:inline">Partner Dashboard</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-white/50 truncate max-w-[160px]" data-testid="partner-email">{user?.email}</span>
          <button
            onClick={logout}
            className="px-3 py-1.5 bg-rose-500/20 text-rose-200 rounded-lg hover:bg-rose-500/30 transition-colors text-sm"
            data-testid="partner-logout-btn"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <RefreshCw className="w-8 h-8 text-charcoal-900/30 animate-spin" />
          </div>
        ) : (
          <>
            {/* Share banner */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl text-charcoal-900">
                  {user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Your booths'}
                </h1>
                <p className="text-charcoal-800/60 text-sm mt-1">
                  Revenue is shown net of refunds. Your share:{' '}
                  <span className="font-semibold text-charcoal-900" data-testid="partner-share-percent">
                    {overview?.share_percent ?? 0}%
                  </span>
                </p>
              </div>
            </div>

            {/* Booths */}
            <div className="flex flex-wrap gap-2 mb-8" data-testid="partner-booths">
              {(overview?.booths || []).map((b) => (
                <div key={b.id} className="flex items-center gap-2 bg-white border border-charcoal-900/10 rounded-xl px-3 py-2 shadow-sm" data-testid={`partner-booth-${b.id}`}>
                  <span className={`w-2 h-2 rounded-full ${b.online ? 'bg-green-500' : 'bg-charcoal-900/20'}`} />
                  <span className="text-sm font-medium text-charcoal-900">{b.name}</span>
                  {b.location && (
                    <span className="text-xs text-charcoal-800/40 flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />{b.location}
                    </span>
                  )}
                </div>
              ))}
              {(overview?.booths || []).length === 0 && (
                <p className="text-sm text-charcoal-800/50">No booths assigned yet — contact Piczoo.</p>
              )}
            </div>

            {/* Period cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
              {Object.entries(PERIOD_LABELS).map(([key, label]) => {
                const p = overview?.periods?.[key];
                return (
                  <div key={key} className="bg-white rounded-2xl border border-charcoal-900/10 shadow-sm p-4 sm:p-5" data-testid={`partner-period-${key}`}>
                    <p className="text-xs uppercase tracking-wider text-charcoal-800/50 mb-2">{label}</p>
                    <p className="text-xl sm:text-2xl font-semibold text-charcoal-900 flex items-center" data-testid={`partner-share-${key}`}>
                      {inr(p?.share)}
                    </p>
                    <p className="text-[11px] text-charcoal-800/40 mb-3">your share</p>
                    <div className="space-y-1 text-xs text-charcoal-800/60">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />Net revenue</span>
                        <span className="font-medium text-charcoal-900">{inr(p?.net)}</span>
                      </div>
                      {(p?.refunds || 0) > 0 && (
                        <div className="flex items-center justify-between text-red-600/70">
                          <span>Refunds</span>
                          <span>−{inr(p?.refunds)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />Sessions</span>
                        <span>{p?.orders ?? 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1"><Printer className="w-3 h-3" />Prints</span>
                        <span>{p?.prints ?? 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Monthly statement */}
            <h2 className="font-serif text-xl sm:text-2xl text-charcoal-900 mb-4">Monthly statement</h2>
            <div className="bg-white rounded-2xl border border-charcoal-900/10 shadow-sm overflow-x-auto" data-testid="partner-statement">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-charcoal-800/50 border-b border-charcoal-900/5">
                    <th className="px-4 sm:px-5 py-3">Month</th>
                    <th className="px-3 py-3 text-right">Sessions</th>
                    <th className="px-3 py-3 text-right">Gross</th>
                    <th className="px-3 py-3 text-right">Refunds</th>
                    <th className="px-3 py-3 text-right">Net</th>
                    <th className="px-4 sm:px-5 py-3 text-right">Your payout ({statement?.share_percent ?? 0}%)</th>
                  </tr>
                </thead>
                <tbody>
                  {(statement?.months || []).map((m) => (
                    <tr key={m.month} className="border-b border-charcoal-900/5 last:border-0" data-testid={`statement-row-${m.month}`}>
                      <td className="px-4 sm:px-5 py-3 font-medium text-charcoal-900">{monthLabel(m.month)}</td>
                      <td className="px-3 py-3 text-right text-charcoal-800/70">{m.orders}</td>
                      <td className="px-3 py-3 text-right text-charcoal-800/70">{inr(m.gross)}</td>
                      <td className="px-3 py-3 text-right text-red-600/70">{m.refunds ? `−${inr(m.refunds)}` : '—'}</td>
                      <td className="px-3 py-3 text-right font-medium text-charcoal-900">{inr(m.net)}</td>
                      <td className="px-4 sm:px-5 py-3 text-right font-semibold text-green-700" data-testid={`payout-${m.month}`}>{inr(m.payout)}</td>
                    </tr>
                  ))}
                  {(statement?.months || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-charcoal-800/40">No revenue recorded yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-charcoal-800/40 mt-3">
              All figures use IST calendar days. Payouts are settled by Piczoo as per your agreement.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
